import closeTokens from '@amzn/meridian-tokens/base/icon/close-small';
import menuMeatballTokens from '@amzn/meridian-tokens/base/icon/menu-meatball';
import Alert from '@amzn/meridian/alert';
import Box from '@amzn/meridian/box';
import Button from '@amzn/meridian/button';
import Column from '@amzn/meridian/column';
import Icon from '@amzn/meridian/icon';
import Link from '@amzn/meridian/link';
import Modal from '@amzn/meridian/modal';
import Row from '@amzn/meridian/row';
import Text from '@amzn/meridian/text';
import React, { useCallback, useContext, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useNavigate, useParams } from 'react-router';

import DataSource from '../../../api';
import { StateContext } from '../../../GlobalStateStore';
import { ExtendedUser } from '../../../Types';
import DataState from '../../../Types/DataState';
import { renderUrl, urls } from '../../../config/Url';
import UserBadge from './UserBadge';
import useSyncedState from '../../../hooks/useSyncedState';

interface ProfilesProps {
  sharedProfiles: ExtendedUser[];
}

const maxDisplayedProfiles = 10;
const apiNaws = DataSource.getDataFeed('NAWS');

const Profiles: React.FC<ProfilesProps> = ({
  sharedProfiles,
}: ProfilesProps): React.ReactElement => {
  const intl = useIntl();
  let { userId } = useParams() as { userId: string };
  const navigate = useNavigate();
  const globalState = useContext(StateContext);
  const {
    state: { inspireOptions },
  } = globalState;
  const { userID, cscEntityPrefix } = inspireOptions.data;
  userId = userID;
  const [profiles, setProfiles] =
    useSyncedState<ExtendedUser[]>(sharedProfiles);
  const [removeState, setRemoveState] = useState(DataState.LOADED);

  const [open, setOpen] = useState(false);
  const onClickButton = useCallback(() => setOpen(true), []);
  const onClose = useCallback(() => {
    setOpen(false);
    window.csa('Content', {
      /* Define a virtual element */
      element: {
        type: 'modal',
        customAttribute: 'customValue',
      },
    });
    window.csa('Interactions');
  }, []);

  const onRemoveProfile = (profileId: string): void => {
    if (userId) {
      const removeCoacheeInput = apiNaws.prepareQueryForRemoveCoachee(
        userId,
        profileId,
        cscEntityPrefix
      );

      apiNaws.removeCoachee(
        removeCoacheeInput,
        setRemoveState,
        profiles,
        setProfiles
      );
    }
  };

  const profileCount: number = profiles.length;
  const profileBadges = (
    profileCount <= maxDisplayedProfiles
      ? profiles
      : profiles.slice(0, maxDisplayedProfiles - 1)
  ).map((profile: ExtendedUser) => {
    return (
      <Box key={profile.userName}>
        <Row spacing='none' spacingInset='200 300 200 none'>
          <Box width='34.9px' height='36px'>
            <UserBadge
              username={profile.userName}
              givenName={profile.displayName}
              tooltip
              link={renderUrl(`${urls.dashboard}/${profile.userID}`)}
            />
          </Box>
        </Row>
      </Box>
    );
  });

  profileBadges.push(
    <Box key='more'>
      <Row spacing='none' spacingInset='200 300 200 none'>
        <Box width='34.9px' height='36px'>
          <Button
            data-csa-c-type='button'
            data-csa-c-slot-id='dashboard-profiles-button-slot'
            data-csa-c-content-id='dashboard-profile-open-button'
            type='secondary'
            onClick={onClickButton}
          >
            <Icon tokens={menuMeatballTokens} />
          </Button>
          <Modal
            title={intl.formatMessage({
              id: 'PerfPortal_profiles_shared_with_you',
              defaultMessage: 'Profiles shared with you',
            })}
            open={open}
            onClose={onClose}
            scrollContainer='viewport'
            closeLabel={intl.formatMessage({
              id: 'cs_perfportal_popover_close',
              defaultMessage: 'close',
            })}
            aria-describedby='modal-description'
            width={300}
          >
            {removeState === DataState.ERROR && (
              <Alert type='error'>
                <FormattedMessage
                  id='cs_perfportal_somethingWentWrong'
                  defaultMessage='Something went wrong!'
                />
              </Alert>
            )}
            {profiles.map((profile: ExtendedUser) => {
              return (
                <Link
                  data-csa-c-type='link'
                  data-csa-c-slot-id='dashboard-profile-link-slot'
                  data-csa-c-content-id={`dashboard-profile-link-for${profile.userID}`}
                  data-csa-c-profile-id={`${profile.userID}`}
                  href={renderUrl(`${urls.dashboard}/${profile.userID}`)}
                  onClick={navigate}
                  type='secondary'
                  key={profile.userName}
                >
                  <Box>
                    <Row
                      spacing='none'
                      widths={['grid-2', 'grid-8', 'grid-2']}
                      spacingInset='200 300 200 none'
                    >
                      <Box height='36px' width='36px'>
                        <UserBadge
                          username={profile.userName}
                          givenName={profile.displayName}
                        />
                      </Box>
                      <Box>
                        <Text>{profile.displayName}</Text>
                        <Text>{profile.userName}</Text>
                      </Box>

                      <Box>
                        {profile.removable && (
                          <Button
                            data-csa-c-type='button'
                            data-csa-c-slot-id='dashboard-profiles-button-slot'
                            data-csa-c-content-id='dashboard-profiles-remove-button'
                            data-csa-c-profile-id={`${profile.userID}`}
                            type='secondary'
                            onClick={(): void => {
                              onRemoveProfile(profile.userID);
                            }}
                          >
                            <Icon tokens={closeTokens} />
                          </Button>
                        )}
                      </Box>
                    </Row>
                  </Box>
                </Link>
              );
            })}
          </Modal>
        </Box>
      </Row>
    </Box>
  );

  return (
    <Box>
      <Column spacing='300'>
        <Row>
          <Text type='h100'>
            <FormattedMessage
              id='PerfPortal_profiles_shared_with_you'
              defaultMessage='Profiles shared with you'
            />{' '}
            ({profileCount})
          </Text>
        </Row>
        <Row
          width='100%'
          widths='44px'
          alignmentHorizontal='start'
          wrap='down'
          spacing='none'
        >
          {profileCount === 0 ? (
            <FormattedMessage
              id='cs_perfportal_no_profiles_shared'
              defaultMessage='No profiles are shared with you'
            />
          ) : (
            profileBadges
          )}
        </Row>
      </Column>
    </Box>
  );
};

export default Profiles;
